<template>
  <div class="tag-print">
    <div class="title-box primary-box">
      <div class="title-box__back-container">
        <v-icon @click="goBack">mdi-chevron-left</v-icon>
      </div>
      <div class="title-box__title">
        <h2>Stampa Etichette</h2>
      </div>
    </div>

    <div class="input-container">
      <v-select
          v-model="pageSizeKey"
          :items="pageSizeItems"
          label="Formato pagina"
          density="comfortable"
          hide-details
      />
      <v-text-field
          id="NumeroLotto"
          v-model="numeroLotto"
          label="Numero lotto"
          variant="outlined"
          density="comfortable"
          hide-details
      />
      <v-text-field
          id="DataConfezionamento"
          v-model="dataConfezionamento"
          label="Data confezionamento"
          variant="outlined"
          density="comfortable"
          hide-details
      />
      <v-text-field
          id="DataScadenza"
          v-model="dataScadenza"
          label="Data scadenza"
          variant="outlined"
          density="comfortable"
          hide-details
      />
      <v-text-field
          v-model.number="fontSizePt"
          label="Dimensione Font (pt)"
          type="number"
          variant="outlined"
          density="comfortable"
          hide-details
      />
      <v-btn class="primary-link" style="color: white" @click="printPage">
        Stampa
      </v-btn>

      <div class="config-section">
        <div class="text-h6 mb-2">Configurazione Posizioni (A6)</div>
        <div v-for="box in boxLayout" :key="box.key" class="config-row">
          <div class="config-label">{{ box.label }}</div>
          <v-text-field v-model.number="box.left" label="X" type="number" step="0.1" density="compact" hide-details variant="outlined" />
          <v-text-field v-model.number="box.top" label="Y" type="number" step="0.1" density="compact" hide-details variant="outlined" />
          <v-text-field v-model.number="box.width" label="Larghezza" type="number" step="0.1" density="compact" hide-details variant="outlined" />
          <v-text-field v-model.number="box.height" label="Altezza" type="number" step="0.1" density="compact" hide-details variant="outlined" />
        </div>
        <v-btn size="small" variant="text" color="grey" @click="resetLayout">
          Ripristina Default
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, ref, watch} from "vue";

// --- inputs
const numeroLotto = ref("");
const dataConfezionamento = ref("");
const dataScadenza = ref("");
const fontSizePt = ref(8);

// Load font size from localStorage
const savedFontSize = localStorage.getItem("tagCreatorFontSize");
if (savedFontSize) {
  fontSizePt.value = Number(savedFontSize);
}

watch(fontSizePt, (newVal) => localStorage.setItem("tagCreatorFontSize", newVal));

// --- navigation
function goBack() {
  history.back();
}

// --- page sizes in cm (portrait)
const SIZES = {
  A6: {widthCM: 10.5, heightCM: 14.8},
  A5: {widthCM: 14.8, heightCM: 21.0},
  A4: {widthCM: 21.0, heightCM: 29.7},
};

const pageSizeKey = ref("A6");
const pageSizeItems = ["A6", "A5", "A4"];
const scaleFromA6 = ref(true); // keep layout proportional to A6 by default

const A6 = SIZES.A6;
const cmToPx = (cm, dpi) => Math.round((cm / 2.54) * dpi);

// --- layout configuration
const defaultLayout = [
  // {key: "lotto", label: "Lotto", left: 1.07, top: 13.45, width: 3.34, height: 0.5},
  // {key: "conf", label: "Conf.", left: 4.75, top: 13.45, width: 2.34, height: 0.5},
  // {key: "scad", label: "Scad.", left: 7.38, top: 13.45, width: 2.29, height: 0.5},
  {key: "lotto", label: "Lotto", left: 0.8197, top: 11.25, width: 3.49, height: 0.55},
  {key: "conf", label: "Conf.", left: 4.5027, top: 11.25, width: 2.43, height: 0.55},
  {key: "scad", label: "Scad.", left: 7.17, top: 11.25, width: 2.43, height: 0.55},
];

const boxLayout = ref(JSON.parse(JSON.stringify(defaultLayout)));

// Load from localStorage
const savedLayout = localStorage.getItem("tagCreatorLayout");
if (savedLayout) {
  try {
    boxLayout.value = JSON.parse(savedLayout);
  } catch (e) {
    console.error("Error parsing saved layout", e);
  }
}

// Save to localStorage on change
watch(boxLayout, (newVal) => {
  localStorage.setItem("tagCreatorLayout", JSON.stringify(newVal));
}, {deep: true});

function resetLayout() {
  boxLayout.value = JSON.parse(JSON.stringify(defaultLayout));
}

// Compute boxes for selected page size
const boxesForSelected = computed(() => {
  const sel = SIZES[pageSizeKey.value];
  const layout = boxLayout.value;

  const getText = (key) => {
    if (key === "lotto") return numeroLotto.value;
    if (key === "conf") return dataConfezionamento.value;
    if (key === "scad") return dataScadenza.value;
    return "";
  };

  if (!scaleFromA6.value || pageSizeKey.value === "A6") {
    return layout.map(b => ({
      text: getText(b.key),
      left: b.left,
      top: b.top,
      width: b.width,
      height: b.height,
    }));
  }
  const kx = sel.widthCM / A6.widthCM;
  const ky = sel.heightCM / A6.heightCM;
  return layout.map(b => ({
    text: getText(b.key),
    left: b.left * kx,
    top: b.top * ky,
    width: b.width * kx,
    height: b.height * ky,
  }));
});

// --- drawing
function fitFontSize(ctx, text, fontFamily, maxW, maxH, startPx, minPx = 8) {
  const pad = Math.floor(maxH * 0.15);
  let fontPx = startPx;
  // Cap at max height to prevent vertical overflow
  if (fontPx > maxH * 0.9) fontPx = Math.floor(maxH * 0.9);
  while (fontPx >= minPx) {
    ctx.font = `${fontPx}px ${fontFamily}`;
    if (ctx.measureText(text).width <= maxW - pad * 2) break;
    fontPx -= 1;
  }
  return {fontPx, pad};
}

async function createImageCm(
    {
      sizeKey = "A6",
      dpi = 300,
      background = "#FFFFFF",
      boxes = [],
      fontFamily = "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      drawGuides = true,
      fontSizePt = 8,
    } = {}) {
  const size = SIZES[sizeKey];
  const width = cmToPx(size.widthCM, dpi);
  const height = cmToPx(size.heightCM, dpi);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  // background
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, width, height);

  const targetFontPx = Math.round((fontSizePt * dpi) / 72);

  for (const b of boxes) {
    const x = cmToPx(b.left, dpi);
    const y = cmToPx(b.top, dpi);
    const w = cmToPx(b.width, dpi);
    const h = cmToPx(b.height, dpi);

    if (drawGuides) {
      ctx.lineWidth = Math.max(1, Math.floor(dpi / 300));
      ctx.strokeStyle = "#000000";
      ctx.strokeRect(x, y, w, h);
    }

    const {fontPx, pad} = fitFontSize(ctx, b.text ?? "", fontFamily, w, h, targetFontPx);
    ctx.font = `${fontPx}px ${fontFamily}`;
    ctx.fillStyle = "#000000";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(b.text ?? "", x + pad, y + h / 2);
  }

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  const dataUrl = canvas.toDataURL("image/png");
  return {canvas, blob, dataUrl};
}

async function printPage() {
  const {dataUrl} = await createImageCm({
    sizeKey: pageSizeKey.value,
    dpi: 300,
    boxes: boxesForSelected.value,
    background: "#FFFFFF",
    drawGuides: false, // set to false to hide borders
    fontSizePt: fontSizePt.value,
  });

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(`
      <html>
        <head>
          <title>Stampa Etichette</title>
          <style>
            @page { margin: 0; }
            body { margin: 0; }
            img { width: 100%; height: auto; display: block; }
          </style>
        </head>
        <body>
          <img src="${dataUrl}" onload="setTimeout(function() { window.print(); window.close(); }, 500)" />
        </body>
      </html>
    `);
    printWindow.document.close();
  }
}
</script>

<style scoped lang="scss">
@use "../style/_variables.scss" as *;

.tag-print {
  text-align: center;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  width: 100%;
  background-color: $secondary-background-color;
}

.input-container {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 10px;
  min-width: 550px;

}

.config-section {
  margin-top: 20px;
  border-top: 1px solid #ccc;
  padding-top: 10px;
}

.config-row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;

  .config-label {
    width: 60px;
    font-size: 0.85rem;
    font-weight: bold;
    text-align: left;
  }
}
</style>
